import React, { useState, useEffect, useContext } from 'react'
import { useSelector } from 'react-redux'
import { selectToken } from 'containers/authentication/authenticationSlice'
import ScreenLoading from 'components/ScreenLoading'
import api from 'containers/api'
import OrganizationsContext from 'containers/organizations/organizationsContext'

interface Subscription {
  type: 'shopify' | 'stripe' | 'azure_marketplace'
  id: string
  plan_name?: string
  status: string
  price_amount?: number
  discounted_price?: number
  discount_percentage?: number | null
  discount_intervals?: number | null
  currency_code?: string
  billing_interval?: string
  shop_domain?: string
  stripe_price_id?: string
  needs_payment_method?: boolean
  activated_at?: string
  created_at?: string
  cancel_at?: string
  trial_ends_at?: string
  current_period_end?: string
}

const SubscriptionsTab = () => {
  const token = useSelector(selectToken)
  const { currentOrganization } = useContext(OrganizationsContext)
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [cancelling, setCancelling] = useState(false)
  const [deletingData, setDeletingData] = useState(false)
  const [deleteSuccess, setDeleteSuccess] = useState(false)

  useEffect(() => {
    fetchSubscriptions()
  }, [currentOrganization?.id])

  const fetchSubscriptions = async () => {
    if (!currentOrganization?.id) return

    try {
      setLoading(true)
      setError('')
      const response = await api.getAllSubscriptions({
        organizationId: currentOrganization.id,
        token
      })
      setSubscriptions(response.subscriptions || [])
    } catch (err: any) {
      console.error('Error fetching subscriptions:', err)
      setError('Failed to load subscriptions')
    } finally {
      setLoading(false)
    }
  }

  const handleCancelShopify = async (subscription: Subscription) => {
    if (!window.confirm(
      `Are you sure you want to cancel your ${subscription.plan_name || 'Shopify'} subscription?\n\n` +
      `This will immediately cancel your subscription and you will lose access to premium features.\n\n` +
      `Your Shopify analytics data will also be permanently deleted.`
    )) {
      return
    }

    try {
      setCancelling(true)
      setError('')
      await api.cancelShopifySubscription({
        organizationId: currentOrganization.id,
        token
      })
      await fetchSubscriptions()
    } catch (err: any) {
      console.error('Error cancelling subscription:', err)
      setError(err?.response?.data?.detail?.message || 'Failed to cancel subscription')
    } finally {
      setCancelling(false)
    }
  }

  const handleDeleteData = async () => {
    if (!window.confirm(
      'Are you sure you want to permanently delete all your analytics data?\n\n' +
      'This action is IRREVERSIBLE. All your historical analytics data will be permanently removed.\n\n' +
      'Type OK to confirm.'
    )) {
      return
    }

    try {
      setDeletingData(true)
      setError('')
      setDeleteSuccess(false)
      await api.requestDataDeletion({
        organizationId: currentOrganization.id,
        token
      })
      setDeleteSuccess(true)
    } catch (err: any) {
      console.error('Error requesting data deletion:', err)
      setError(err?.response?.data?.detail?.message || 'Failed to request data deletion')
    } finally {
      setDeletingData(false)
    }
  }

  const hasActiveSubscription = (): boolean => {
    return subscriptions.some(s => {
      const status = s.status.toLowerCase()
      return status === 'active' || status === 'trialing' ||
        status === 'pending_approval' || status === 'subscribed'
    })
  }

  const getStatusColor = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active':
        return '#28a745'
      case 'trialing':
        return '#17a2b8'
      case 'pending_approval':
      case 'pending':
        return '#ffc107'
      case 'cancelled':
      case 'canceled':
        return '#dc3545'
      case 'subscribed':
        return '#28a745'
      default:
        return '#6c757d'
    }
  }

  const getStatusLabel = (status: string): string => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'Active'
      case 'trialing':
        return 'Trial'
      case 'pending_approval':
        return 'Pending Approval'
      case 'cancelled':
      case 'canceled':
        return 'Cancelled'
      case 'subscribed':
        return 'Subscribed'
      default:
        return status.charAt(0).toUpperCase() + status.slice(1)
    }
  }

  const getTypeIcon = (type: string): string => {
    switch (type) {
      case 'shopify':
        return 'shopping_bag'
      case 'stripe':
        return 'credit_card'
      case 'azure_marketplace':
        return 'cloud'
      default:
        return 'payment'
    }
  }

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'shopify':
        return 'Shopify'
      case 'stripe':
        return 'Stripe'
      case 'azure_marketplace':
        return 'Azure Marketplace'
      default:
        return type
    }
  }

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const formatInterval = (interval: string | undefined): string => {
    switch (interval) {
      case 'EVERY_30_DAYS':
        return 'Monthly'
      case 'ANNUAL':
        return 'Annual'
      default:
        return interval || 'N/A'
    }
  }

  const formatCurrency = (amount: number | undefined, currency: string | undefined): string => {
    if (amount === undefined) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD'
    }).format(amount)
  }

  // e.g. "100% off for 6 months", "50% off" (no end)
  const formatDiscount = (subscription: Subscription): string => {
    const unit = subscription.billing_interval === 'ANNUAL' ? 'year' : 'month'
    const n = subscription.discount_intervals
    const duration = n ? ` for ${n} ${unit}${n === 1 ? '' : 's'}` : ''
    return `${subscription.discount_percentage}% off${duration}`
  }

  const getShopifyDashboardUrl = (shopDomain: string | undefined): string => {
    if (!shopDomain) return '#'
    return `https://${shopDomain}/admin/settings/billing`
  }

  return (
    <div style={{ marginTop: '1rem', width: '100%', maxWidth: '900px', padding: '0 1rem', boxSizing: 'border-box' }}>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#d1ecf1',
          border: '1px solid #bee5eb',
          borderRadius: '4px',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="material-icons-outlined" style={{ color: '#0c5460' }}>
            info
          </span>
          <strong style={{ color: '#0c5460' }}>Subscription Management</strong>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', color: '#0c5460', fontSize: '0.9rem', wordBreak: 'break-word' }}>
          View and manage your organization's subscriptions. You can cancel subscriptions or view billing details from here.
        </p>
      </div>

      {error && (
        <div
          style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            marginBottom: '1rem',
            color: '#721c24',
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <ScreenLoading />
        </div>
      ) : subscriptions.length === 0 ? (
        <div
          style={{
            padding: '3rem',
            textAlign: 'center',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
          }}
        >
          <span
            className="material-icons-outlined"
            style={{ fontSize: '4rem', color: '#6c757d' }}
          >
            credit_card_off
          </span>
          <p style={{ marginTop: '1rem', color: '#6c757d', fontSize: '1.1rem' }}>
            No active subscriptions
          </p>
          <p style={{ color: '#999', fontSize: '0.9rem' }}>
            Subscribe to a plan to unlock premium features for your organization.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {subscriptions.map((subscription) => (
            <div
              key={`${subscription.type}-${subscription.id}`}
              style={{
                padding: '1.5rem',
                border: subscription.status === 'active' || subscription.status === 'subscribed'
                  ? '2px solid #28a745'
                  : '1px solid #e0e0e0',
                borderRadius: '12px',
                backgroundColor: '#fff',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
              }}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1.5rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: subscription.type === 'shopify' ? '#96bf48' : '#635bff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span className="material-icons-outlined" style={{ color: 'white', fontSize: '24px' }}>
                      {getTypeIcon(subscription.type)}
                    </span>
                  </div>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
                      {subscription.plan_name || getTypeLabel(subscription.type)}
                    </h3>
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      via {getTypeLabel(subscription.type)}
                    </span>
                  </div>
                </div>

                <div style={{
                  padding: '0.35rem 1rem',
                  backgroundColor: getStatusColor(subscription.status) + '20',
                  color: getStatusColor(subscription.status),
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.35rem'
                }}>
                  <span style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(subscription.status)
                  }} />
                  {getStatusLabel(subscription.status)}
                </div>
              </div>

              {/* Details Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1.25rem',
                marginBottom: '1.5rem'
              }}>
                {subscription.price_amount !== undefined && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      Price
                    </div>
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
                      {formatCurrency(
                        subscription.discount_percentage ? subscription.discounted_price : subscription.price_amount,
                        subscription.currency_code
                      )}
                      <span style={{ fontSize: '0.85rem', fontWeight: 400, color: '#666' }}>
                        /{formatInterval(subscription.billing_interval).toLowerCase()}
                      </span>
                    </div>
                    {!!subscription.discount_percentage && (
                      <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '0.25rem' }}>
                        {formatDiscount(subscription)}, then {formatCurrency(subscription.price_amount, subscription.currency_code)}
                      </div>
                    )}
                  </div>
                )}

                {subscription.billing_interval && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      Billing Cycle
                    </div>
                    <div style={{ fontSize: '1rem', color: '#333' }}>
                      {formatInterval(subscription.billing_interval)}
                    </div>
                  </div>
                )}

                {subscription.activated_at && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      Started
                    </div>
                    <div style={{ fontSize: '1rem', color: '#333' }}>
                      {formatDate(subscription.activated_at)}
                    </div>
                  </div>
                )}

                {subscription.trial_ends_at && new Date(subscription.trial_ends_at) > new Date() && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#17a2b8', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      Trial Ends
                    </div>
                    <div style={{ fontSize: '1rem', color: '#17a2b8', fontWeight: 500 }}>
                      {formatDate(subscription.trial_ends_at)}
                    </div>
                  </div>
                )}

                {subscription.current_period_end && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      {subscription.cancel_at ? 'Access Until' : 'Next Billing'}
                    </div>
                    <div style={{ fontSize: '1rem', color: subscription.cancel_at ? '#dc3545' : '#333', fontWeight: subscription.cancel_at ? 500 : 400 }}>
                      {formatDate(subscription.current_period_end)}
                    </div>
                  </div>
                )}

                {subscription.cancel_at && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      Cancelled On
                    </div>
                    <div style={{ fontSize: '1rem', color: '#dc3545' }}>
                      {formatDate(subscription.cancel_at)}
                    </div>
                  </div>
                )}

                {subscription.shop_domain && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#999', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
                      Store
                    </div>
                    <div style={{ fontSize: '1rem', color: '#333', wordBreak: 'break-all' }}>
                      {subscription.shop_domain}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              {(subscription.status === 'active' || subscription.status === 'pending_approval' || subscription.status === 'subscribed') && (
                <div style={{
                  borderTop: '1px solid #eee',
                  paddingTop: '1rem',
                  display: 'flex',
                  gap: '0.75rem',
                  flexWrap: 'wrap'
                }}>
                  {subscription.type === 'shopify' && (
                    <>
                      <a
                        href={getShopifyDashboardUrl(subscription.shop_domain)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.6rem 1.25rem',
                          backgroundColor: '#96bf48',
                          color: 'white',
                          borderRadius: '6px',
                          textDecoration: 'none',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#7ea63d'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#96bf48'}
                      >
                        <span className="material-icons-outlined" style={{ fontSize: '18px' }}>
                          open_in_new
                        </span>
                        Shopify Dashboard
                      </a>

                      <button
                        type="button"
                        onClick={() => handleCancelShopify(subscription)}
                        disabled={cancelling}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          padding: '0.6rem 1.25rem',
                          backgroundColor: 'transparent',
                          color: '#dc3545',
                          border: '1px solid #dc3545',
                          borderRadius: '6px',
                          cursor: cancelling ? 'not-allowed' : 'pointer',
                          fontSize: '0.9rem',
                          fontWeight: 500,
                          opacity: cancelling ? 0.6 : 1,
                          transition: 'all 0.2s'
                        }}
                        onMouseOver={(e) => {
                          if (!cancelling) {
                            e.currentTarget.style.backgroundColor = '#dc3545'
                            e.currentTarget.style.color = 'white'
                          }
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent'
                          e.currentTarget.style.color = '#dc3545'
                        }}
                      >
                        <span className="material-icons-outlined" style={{ fontSize: '18px' }}>
                          {cancelling ? 'hourglass_empty' : 'cancel'}
                        </span>
                        {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                      </button>
                    </>
                  )}

                  {subscription.type === 'stripe' && (
                    <button
                      type="button"
                      onClick={() => window.open('/organization/0/billing/portal', '_blank')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.6rem 1.25rem',
                        backgroundColor: '#635bff',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: 500
                      }}
                    >
                      <span className="material-icons-outlined" style={{ fontSize: '18px' }}>
                        settings
                      </span>
                      Manage in Stripe
                    </button>
                  )}
                </div>
              )}

              {/* Cancelled state actions */}
              {(subscription.status === 'cancelled' || subscription.status === 'canceled') && (
                <div style={{
                  borderTop: '1px solid #eee',
                  paddingTop: '1rem',
                }}>
                  <p style={{ color: '#666', fontSize: '0.9rem', margin: 0 }}>
                    This subscription has been cancelled. Visit the{' '}
                    <a
                      href={
                        subscription.type === 'shopify'
                          ? `/organization/${currentOrganization?.slug}/billing/products?shopify=true&shop=${encodeURIComponent(subscription.shop_domain ?? '')}`
                          : `/organization/${currentOrganization?.slug}/billing/products`
                      }
                      style={{ color: '#16a34a', fontWeight: 600, textDecoration: 'underline' }}
                    >
                      billing page
                    </a>{' '}
                    to subscribe again.
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Data Deletion Section — admin only */}
      {!loading && currentOrganization?.is_admin && (
        <div style={{
          marginTop: '3rem',
          padding: '1.5rem',
          border: '1px solid #f5c6cb',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <span className="material-icons-outlined" style={{ color: '#dc3545', fontSize: '22px' }}>
              warning
            </span>
            <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#dc3545' }}>
              Delete My Data
            </h3>
          </div>
          <p style={{ color: '#666', fontSize: '0.9rem', margin: '0 0 1rem 0', lineHeight: '1.5' }}>
            If you decide to unsubscribe, you have the option of keeping your data in a Maya managed
            data warehouse for future reference, or moving it to your own data warehouse.
            If you still wish to proceed with deletion, this will permanently remove all analytics data
            associated with this organization and cannot be undone.
          </p>

          {deleteSuccess && (
            <div style={{
              padding: '0.75rem 1rem',
              backgroundColor: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              color: '#155724',
              fontSize: '0.9rem',
              marginBottom: '1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}>
              <span className="material-icons-outlined" style={{ fontSize: '18px' }}>check_circle</span>
              Data deletion has been scheduled. This may take a few minutes to complete.
            </div>
          )}

          <button
            type="button"
            onClick={handleDeleteData}
            disabled={deletingData || hasActiveSubscription() || deleteSuccess}
            title={hasActiveSubscription() ? 'Cancel your subscription before deleting data' : ''}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.6rem 1.25rem',
              backgroundColor: 'transparent',
              color: '#dc3545',
              border: '1px solid #dc3545',
              borderRadius: '6px',
              cursor: (deletingData || hasActiveSubscription() || deleteSuccess) ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: 500,
              opacity: (deletingData || hasActiveSubscription() || deleteSuccess) ? 0.5 : 1,
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              if (!deletingData && !hasActiveSubscription() && !deleteSuccess) {
                e.currentTarget.style.backgroundColor = '#dc3545'
                e.currentTarget.style.color = 'white'
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent'
              e.currentTarget.style.color = '#dc3545'
            }}
          >
            <span className="material-icons-outlined" style={{ fontSize: '18px' }}>
              {deletingData ? 'hourglass_empty' : 'delete_forever'}
            </span>
            {deletingData ? 'Scheduling deletion...' : deleteSuccess ? 'Deletion scheduled' : 'Delete my data'}
          </button>

          {hasActiveSubscription() && (
            <p style={{ color: '#999', fontSize: '0.8rem', marginTop: '0.5rem', marginBottom: 0 }}>
              You must cancel your active subscription before deleting data.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default SubscriptionsTab
